import * as _ from "lodash-es";
import moment from "moment-timezone";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { FormattedNumber } from "react-intl";
import UserTypesEnum from "../../../../models/UserTypesEnum";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwSelectOption from "../../../shared/OwSelectOption/OwSelectOption";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import CommissionReportRow from "./CommissionReportRow/CommissionReportRow";
import ReportPeriod from "../ReportPeriod/ReportPeriod";
import UIService from "../../../../services/UIService";
import ExcelJS from "exceljs";
import IProcessed_Report from "../../../../models/IProcessed_Report";
import { debounce } from "lodash-es";
import { useAppContextNonNull } from "@contexts/AppContext";

type Props = {
    processedReport: IProcessed_Report;
};

export const CommissionReport = (props: Props) => {
    const { userModel, supplierModel, socket } = useAppContextNonNull();

    const [selectedUserGuid, setSelectedUserGuid] = useState<string | undefined>(undefined);

    const chartRef = useRef<HTMLDivElement>(null);
    const [pieChart, setPieChart] = useState<any>(null);

    // componentDidUpdate(prevProps: Props, prevState: State) {
    //     // Re-render chart if the report data or selected user changes
    //     if (prevProps.processedReport !== props.processedReport || prevState.selectedUserGuid !== state.selectedUserGuid) {
    //         renderPieChart();
    //     }
    // }

    // Add resize listener for responsive behavior
    const handleResize = debounce(() => {
        renderPieChart();
    }, 300);

    useEffect(() => {
        renderPieChart();
        window.addEventListener("resize", handleResize);

        return () => {
            if (chartRef.current) {
                d3.select(chartRef.current).selectAll("*").remove();
            }

            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const downloadExcel = () => {
        if (props.processedReport) {
            const workbook = new ExcelJS.Workbook();
            const ws = workbook.addWorksheet("Commissions");

            const salesPeopleWs: { salesperson: string; ws: ExcelJS.Worksheet }[] = [];

            ws.columns = [
                { header: "Salesperson", key: "salesperson" },
                { header: "Email", key: "email" },
                { header: "Commission", key: "commission" },
            ];

            ws.getCell("A1").font = {
                bold: true,
            };

            ws.getCell("B1").font = {
                bold: true,
            };

            ws.getCell("C1").font = {
                bold: true,
            };

            const userRows = Object.keys(props.processedReport.report_data.userDetailsObj);

            for (const user of userRows) {
                const userDetail = props.processedReport.report_data.userDetailsObj[user];

                let name = userDetail?.name;
                const email = userDetail?.email;
                // tslint:disable-next-line:variable-name
                const commission_total = userDetail?.grandTotal || 0;

                if (workbook.getWorksheet(name)) {
                    name = `${userDetail?.name} (2)`;
                }

                ws.addRow({ salesperson: name, email, commission: commission_total });

                const salesPersonWs = workbook.addWorksheet(name);

                salesPersonWs.columns = [
                    { header: "Account", key: "account" },
                    { header: "Order", key: "order" },
                    { header: "Order Sub Total", key: "orderSubTotal" },
                    { header: "Amount", key: "amount" },
                ];

                salesPersonWs.getCell("A1").font = {
                    bold: true,
                };

                salesPersonWs.getCell("B1").font = {
                    bold: true,
                };

                salesPersonWs.getCell("C1").font = {
                    bold: true,
                };

                salesPersonWs.getCell("D1").font = {
                    bold: true,
                };

                const vendorGuids = Object.keys(userDetail.vendors);

                for (const vendorGuid of vendorGuids) {
                    const vendorDetail = userDetail.vendors[vendorGuid];

                    const grandTotal = vendorDetail.grandTotal;
                    const account = vendorDetail.name;

                    const orders = vendorDetail.orders;

                    for (const order of orders) {
                        const orderName = order.name;
                        const commission = order.commission;
                        const orderGuid = order.guid;
                        const orderSubTotal = order.orderSubTotal;

                        salesPersonWs.addRow({ account, order: orderName, orderSubTotal, amount: commission });
                    }
                }

                salesPeopleWs.push({ salesperson: name, ws: salesPersonWs });
            }

            const periodStart = moment(props.processedReport.period_start).format("YYYY-MM-DD");
            const periodEnd = moment(props.processedReport.period_end).format("YYYY-MM-DD");

            workbook.xlsx.writeBuffer().then((data: any) => {
                const blob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                const url = window.URL.createObjectURL(blob);
                const anchor = document.createElement("a");
                anchor.href = url;
                anchor.download = `${periodStart}-${periodEnd}-orderwerks-commission-report.xlsx`;
                anchor.click();
                window.URL.revokeObjectURL(url);
            });
        }
    };

    const downloadCSV = () => {
        if (props.processedReport?.report_data) {
            const headerRow = ["Salesperson", "Email", "Commission"];

            const dataRows: any[][] = [];

            const userRows = Object.keys(props.processedReport.report_data?.userDetailsObj);

            for (const user of userRows) {
                const userDetail = props.processedReport.report_data?.userDetailsObj[user];

                const name = userDetail?.name;
                const email = userDetail?.email;
                const commission_total = userDetail?.grandTotal;

                const userRow = [name, email, commission_total];

                dataRows.push(userRow);
            }

            const periodStart = moment(props.processedReport.period_start).format("YYYY-MM-DD");
            const periodEnd = moment(props.processedReport.period_end).format("YYYY-MM-DD");

            const rows = [headerRow, ...dataRows, [`${periodStart} - ${periodEnd}`, "", props.processedReport.report_data.grandTotal]];

            const csvContent = "data:text/csv;charset=utf-8," + rows.map((e) => e.join(",")).join("\n");

            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", `${periodStart}-${periodEnd}-orderwerks-commission-report.csv`);

            link.click();
        }
    };

    const renderPieChart = () => {
        if (!props.processedReport.report_data || !chartRef.current) {
            return;
        }

        // Clear any existing chart
        d3.select(chartRef.current).selectAll("*").remove();

        const userGuids = Object.keys(props.processedReport.report_data.userDetailsObj);

        const users = _.map(userGuids, (guid: string) => {
            const userDetailsObj = props.processedReport.report_data!.userDetailsObj;
            const userDetail = userDetailsObj[guid];
            return {
                name: userDetail.name,
                value: Number(userDetail.grandTotal),
            };
        });

        // Chart dimensions - make responsive based on container width
        const containerWidth = chartRef.current.clientWidth;
        const containerHeight = chartRef.current.clientHeight;
        const width = Math.min(containerWidth, 600); // Cap at 600px for larger screens
        const height = 450;
        const margin = 50; // Increased margin for labels

        // The radius of the pie chart is half the smallest side
        const radius = Math.min(width, height) / 2 - margin;

        // Create the SVG container
        const svg = d3
            .select(chartRef.current)
            .append("svg")
            .attr("width", width)
            .attr("height", height)
            .append("g")
            .attr("transform", `translate(${width / 2},${height / 2})`);

        const chartTextColor = UIService.isMainViewLight() ? "black" : "white";

        // Add title
        svg.append("text")
            .attr("text-anchor", "middle")
            .attr("y", height / 2 - 10)
            .attr("font-size", "16px")
            .attr("font-weight", "bold")
            .style("fill", chartTextColor)
            .text("Commission By User");

        // Set up color scale
        const color = d3.scaleOrdinal(d3.schemeCategory10);

        // Create pie layout
        const pie = d3
            .pie<any>()
            .value((d) => d.value)
            .sort(null);

        // Generate the arcs
        const arc = d3.arc().innerRadius(0).outerRadius(radius);

        // For labels - use a more dynamic approach to prevent overlap
        const labelArc = d3
            .arc()
            .innerRadius(radius * 0.7)
            .outerRadius(radius * 1.0);

        // Add tooltip
        const tooltip = d3
            .select(chartRef.current)
            .append("div")
            .attr("class", "d3-tooltip")
            .style("position", "absolute")
            .style("background-color", "white")
            .style("color", "black")
            .style("border", "1px solid #ddd")
            .style("border-radius", "4px")
            .style("padding", "10px")
            .style("pointer-events", "none")
            .style("opacity", 0)
            .style("z-index", 100);

        // Generate the pie chart
        const arcs = svg.selectAll(".arc").data(pie(users)).enter().append("g").attr("class", "arc");

        // Draw the arcs
        arcs.append("path")
            .attr("d", arc as any)
            .attr("fill", (d, i) => color(i.toString()))
            .attr("stroke", "#fff")
            .style("stroke-width", "2px")
            .on("mouseover", function (event, d) {
                tooltip.transition().duration(200).style("opacity", 0.9);
                tooltip
                    .html(`${d.data.name}: $${d.data.value.toFixed(2)}`)
                    .style("left", event.pageX + 10 + "px")
                    .style("top", event.pageY - 28 + "px");
            })
            .on("mouseout", function () {
                tooltip.transition().duration(500).style("opacity", 0);
            });

        // Improved label positioning to avoid overlap
        const totalValue = _.sumBy(users, "value");
        const angleOffset = 0.1; // Small offset to adjust positions

        // Create text label elements
        const textLabels = arcs
            .append("text")
            .attr("class", "pie-label")
            .style("font-size", "12px")
            .style("font-weight", "500")
            .style("fill", chartTextColor)
            .style("pointer-events", "none");

        // Add label lines
        arcs.append("polyline")
            .attr("class", "label-line")
            .style("fill", "none")
            .style("stroke", "#999")
            .style("stroke-width", "1px")
            .style("opacity", (d) => (d.data.value > totalValue * 0.03 ? 1 : 0))
            .attr("points", function (d) {
                const pos = labelArc.centroid(d as any);
                const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
                const x = Math.sin(midAngle) * (radius + 20);
                const y = -Math.cos(midAngle) * (radius + 20);

                // Convert array of points to a string format
                const pointsArray = [arc.centroid(d as any), labelArc.centroid(d as any), [x, y]];
                return pointsArray.map((point) => point[0] + "," + point[1]).join(" ");
            });

        // Add the actual text labels at the end of the lines
        textLabels.each(function (d) {
            const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
            const x = Math.sin(midAngle) * (radius + 30);
            const y = -Math.cos(midAngle) * (radius + 30);

            // Only show label if the slice is big enough
            if (d.data.value > totalValue * 0.03) {
                d3.select(this)
                    .attr("transform", `translate(${x}, ${y})`)
                    .attr("text-anchor", x > 0 ? "start" : "end")
                    .text(d.data.name);
            }
        });
    };

    const getCommissionByUserPieChart = () => {
        if (!props.processedReport.report_data) {
            return null;
        }

        return (
            <div className={"mt-2 d-flex justify-content-center"}>
                <div ref={chartRef} style={{ width: "100%", maxWidth: "700px", height: "450px" }}></div>
            </div>
        );
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;
        const name = e.target.name;

        switch (name) {
            case "selectedUserGuid":
                setSelectedUserGuid(value);
                break;
        }
    };

    const getReportContent = (usersArray: any, userGuids: string[]) => {
        if (!props.processedReport.report_data) {
            return null;
        }

        const userDetailsObj: any = props.processedReport.report_data ? props.processedReport.report_data.userDetailsObj : {};

        let grandTotal: number | undefined;

        if (selectedUserGuid) {
            usersArray.forEach((detail: any) => {
                if (detail.guid === selectedUserGuid) {
                    grandTotal = detail.grandTotal;
                }
            });
        }

        return (
            <div>
                <div>
                    <div className={"d-flex"}>
                        <OwLabel title={"total_commissions"} className={"me-auto"} />
                        <span>
                            <FormattedNumber
                                value={Number(grandTotal || props.processedReport.report_data.grandTotal)}
                                minimumFractionDigits={2}
                            />
                        </span>
                    </div>
                    <div>
                        <OwTitle title={"breakdown"} tag={"h5"} />
                        {_.map(userGuids, (userGuid: string, index: number) => {
                            if (selectedUserGuid) {
                                if (userGuid !== selectedUserGuid) {
                                    return null;
                                }
                            }

                            return <CommissionReportRow key={index} userDetailRow={userDetailsObj[userGuid]} />;
                        })}
                    </div>
                    <div></div>
                </div>
                {usersArray.length > 1 ? <div>{getCommissionByUserPieChart()}</div> : null}
            </div>
        );
    };

    const userGuids = props.processedReport.report_data ? Object.keys(props.processedReport.report_data.userDetailsObj) : [];
    const usersArray: any[] = userGuids.map((guid) => {
        return {
            guid,
            name: props.processedReport.report_data?.userDetailsObj[guid].name,
            grandTotal: props.processedReport.report_data?.userDetailsObj[guid].grandTotal,
        };
    });

    return (
        <div className="CommissionReport">
            <div className={"d-flex"}>
                <OwTitle title={"commission_report"} leftIconName={"fad fa-money-check-edit-alt"} tag={"h2"} />
                <OwTitle title={": " + props.processedReport.title} disableTranslation={true} tag={"h2"} />
            </div>

            <ReportPeriod className={"mb-3"} startDate={props.processedReport.period_start} endDate={props.processedReport.period_end} />

            {props.processedReport.report_data ? (
                <div>
                    <div>
                        <OwLabel className={"me-3"} title={"excel"} leftIconClass={"fas fa-download"} fn={downloadExcel} />
                        <OwLabel title={"csv"} leftIconClass={"fas fa-download"} fn={downloadCSV} />
                    </div>
                    {userModel.isSupplierUser && userModel.supplierUserToken?.user_type_id === UserTypesEnum.ADMIN ? (
                        <div className={"mb-2"}>
                            <OwLabel title={"salesperson"} noBottomMargin={true} />
                            <div className={".g-0 col-12 col-md-4 col-lg-3"}>
                                <OwSelectOption
                                    titleProp={"name"}
                                    valueProp={"guid"}
                                    options={usersArray}
                                    name={"selectedUserGuid"}
                                    fn={handleChange}
                                    showBlankFirst={true}
                                />
                            </div>
                        </div>
                    ) : null}
                    {getReportContent(usersArray, userGuids)}
                </div>
            ) : null}
        </div>
    );
};

export default CommissionReport;
